import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, ApiError } from '../../api/client'
import { Button, Field } from '../../ui/primitives'

/**
 * Accesso a KPO: si chiede il link via e-mail, oppure si inserisce il codice a sei cifre che
 * arriva insieme al link. Il login è di KPO e non della Dashboard, di proposito: stessi utenti,
 * sessioni separate — chi è dentro l'una non è automaticamente dentro l'altra.
 */
export function LoginPage() {
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [otp, setOtp] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string>()

  const chiediLink = async () => {
    if (!email.trim()) return
    setBusy(true)
    setProblem(undefined)
    try {
      await api.post('/auth/magic', { email: email.trim() })
      setSent(true)
    } catch {
      // La richiesta del link non fallisce mai per colpa dell'indirizzo (risposta uniforme):
      // se arriva un errore è un guasto vero.
      setProblem('Richiesta non riuscita: riprova fra qualche istante.')
    } finally {
      setBusy(false)
    }
  }

  const entraConCodice = async () => {
    setBusy(true)
    setProblem(undefined)
    try {
      await api.post('/auth/verify', { email: email.trim(), otp: otp.trim() })
      navigate('/', { replace: true })
    } catch (e: unknown) {
      setProblem(e instanceof ApiError ? (e.detail ?? e.title) : 'Accesso non riuscito.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="firstrun">
      <div className="firstrun-panel">
        <img src="/logo.svg" alt="KPO — Proposte Ordini e Consegne" style={{ height: 56, marginBottom: 12 }} />
        <p className="muted" style={{ margin: '8px 0 20px' }}>
          Inserisci la tua e-mail di lavoro: ti mandiamo un link e un codice per entrare.
          L'accesso a KPO è separato da quello della Dashboard.
        </p>

        {problem && <div className="notice notice-error">{problem}</div>}

        <Field label="E-mail">
          <input
            type="email"
            value={email}
            autoFocus
            placeholder="nome.cognome@azienda.it"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void chiediLink()}
          />
        </Field>

        <div className="inline" style={{ marginTop: 8 }}>
          <Button variant="primary" disabled={busy || !email.trim()} onClick={() => void chiediLink()}>
            {busy && !sent ? 'Invio…' : sent ? 'Rimanda il link' : 'Mandami il link'}
          </Button>
        </div>

        {sent && (
          <>
            <div className="notice notice-ok" style={{ marginTop: 16 }}>
              <strong>Controlla la posta.</strong>
              <p>
                Se l'indirizzo è abilitato, il link è in arrivo. In alternativa inserisci qui il
                codice a sei cifre che trovi nella stessa e-mail.
              </p>
            </div>

            <Field label="Codice a sei cifre">
              <input
                inputMode="numeric"
                maxLength={6}
                value={otp}
                placeholder="000000"
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && otp.length === 6 && void entraConCodice()}
              />
            </Field>
            <Button variant="primary" disabled={busy || otp.length !== 6} onClick={() => void entraConCodice()}>
              Entra col codice
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

/** Arrivo dal link dell'e-mail: un passaggio esplicito, così i prefetch della posta non consumano il token. */
export function VerifyPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''

  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string>()

  const entra = async () => {
    setBusy(true)
    setProblem(undefined)
    try {
      await api.post('/auth/verify', { token })
      navigate('/', { replace: true })
    } catch (e: unknown) {
      setProblem(e instanceof ApiError ? (e.detail ?? e.title) : 'Accesso non riuscito.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="firstrun">
      <div className="firstrun-panel">
        <h1>Entra in KPO</h1>
        {problem ?
          <>
            <div className="notice notice-error">{problem}</div>
            <Button onClick={() => navigate('/login', { replace: true })}>Chiedi un nuovo link</Button>
          </>
        : <>
            <p className="muted" style={{ margin: '8px 0 20px' }}>
              Premi il pulsante per completare l'accesso su questo dispositivo.
            </p>
            <Button variant="primary" disabled={busy || !token} onClick={() => void entra()}>
              {busy ? 'Un istante…' : 'Entra'}
            </Button>
          </>
        }
      </div>
    </div>
  )
}
